import http from '@/utils/http';
import {
  getUrlCollectionPagePath,
  mergeUrlCollectionData,
  parseUrlCollectionData,
} from '@home/utils/urlCollectionData';

import type {
  IRestoreDefaultUrlCollectionItemParams,
  IRestoreDefaultUrlCollectionItemResult,
} from '@home/types/urlCollection';

/**
 * 直接走全局 http，不通过 services/dashboard.ts、utils/dashboardRequest.ts，
 * 避免链式 import dashboard/utils/utils.ts（它顶层依赖大量业务模块）。
 * 与 services/dashboard.ts 中 getGameListList 等价，只是路径解耦。
 */
const fetchUserMessage = (): Promise<unknown> => {
  return http({
    url: '/api/v1/dashboard/userMessage',
    method: 'post',
    data: { style_version: 'v2' },
    errorHandler: true,
  });
};

/**
 * 页面访问权限。
 * 0: 无权限
 * 1: 有权限
 */
export enum Permission {
  NoPermission = '0',
  HasPermission = '1',
}

/**
 * 页面可用状态。
 */
export enum Status {
  Unavailable = '0',
  Available = '1',
}

/**
 * 实体类型枚举（与首页URL收藏协议保持一致）。
 */
export enum EntityType {
  Mobile = 'mobile',
  PC = 'pc',
  Console = 'console',
  Combine = 'combine',
  Company = 'company',
}

export interface IAnalysisUrlInfoParams {
  url: string;
}

export interface IGameItem {
  game_code: string;
  game_name?: string;
  cover?: string;
  is_access?: boolean;
  data_frame?: string;
  game_type?: string;
  game_List?: IGameItem[];
}

export interface IAnalysisUrlInfoResult {
  success: boolean;
  permission: Permission;
  status: Status;
  entity_type?: EntityType[];
  entity_id?: string;
  entity_name?: string;
  entity_cover?: string;
  page_url?: string;
  page_name?: string;
  data?: string;
}

export interface IGetEntityDetailsByIdParams {
  entity_type?: EntityType[];
  entity_id?: string;
}

export interface IEntityDetailResult {
  entity_type: EntityType[];
  entity_id: string;
  entity_name: string;
  entity_cover: string;
}

interface IGameIndexItem {
  game: IGameItem;
  parentGame?: IGameItem;
}

interface IGameListResponseShape {
  main_game?: IGameItem[];
}

const DASHBOARD_SYSTEM_CODE = 'dashboard';
const DASHBOARD_URL_TYPE_GAME = 'game';
const DASHBOARD_DEFAULT_SHORTCUT_URL =
  '/v2/dashboard/game/:entityId/overview/daily';

interface IDashboardUrlInfo {
  system: string;
  urlType: string;
  urlId: string;
  realPath: string;
}

/**
 * 经分路径解析（与 dashboard/utils/utils.ts 中 parseUrlInfo 行为一致）。
 *
 * 这里 inline 一份的原因：
 * 首页通过动态 import 加载本文件，而 utils.ts 顶层依赖了大量 dashboard
 * 业务模块（_config/constant、NewFilter、filterListMap 等），直接复用会
 * 把整个 utils 树拉进首页 chunk，增大体积并引入循环依赖风险。
 */
const parseUrlInfo = (str = ''): IDashboardUrlInfo => {
  const pathParts = str.trim().split('/').filter(Boolean);
  const prefixOffset = /^v\d+$/i.test(pathParts[0] || '') ? 1 : 0;
  const [system = '', urlType = '', urlId = '', ...arr] = pathParts.slice(
    prefixOffset,
  );
  const realPath = arr.length > 0 ? arr.join('/') : '';
  return { system, urlType, urlId, realPath };
};

/**
 * 会话内最近一次成功拉取的 main_game（无 TTL，刷新页面后重新拉取）。
 * `undefined` 表示尚未成功拉取过。
 */
let cachedMainGameList: IGameItem[] | undefined;

/** 进行中的请求，并发去重共用同一 Promise */
let gameListPendingPromise: Promise<IGameItem[]> | null = null;

const normalizeMainGameFromResponse = (result: unknown): IGameItem[] => {
  const typedResult = (result || {}) as IGameListResponseShape;
  const raw = typedResult.main_game;
  return Array.isArray(raw) ? raw : [];
};

/**
 * 拉取 main_game：
 * - 已成功拉取过则直接返回内存结果（不按时间失效）；
 * - 并发共用同一 Promise；
 * - 成功则写入会话缓存；
 * - 失败时若有历史成功缓存则退回，否则返回空数组。
 */
export const fetchGameListCached = async (): Promise<IGameItem[]> => {
  if (cachedMainGameList !== undefined) {
    return cachedMainGameList;
  }

  if (gameListPendingPromise) {
    return gameListPendingPromise;
  }

  gameListPendingPromise = fetchUserMessage()
    .then((result: unknown) => {
      const list = normalizeMainGameFromResponse(result);
      cachedMainGameList = list;
      return list;
    })
    .catch(() => cachedMainGameList ?? [])
    .finally(() => {
      gameListPendingPromise = null;
    });

  return gameListPendingPromise;
};

/**
 * 构建 game_code -> 游戏信息索引：
 * - 主游戏直接写入索引
 * - 子版本 game_List 作为补充索引（不覆盖同 code 的主游戏）
 */
const buildGameIndexMap = (
  list: IGameItem[],
): Map<string, IGameIndexItem> => {
  const nextMap = new Map<string, IGameIndexItem>();
  list.forEach((item) => {
    if (item?.game_code) {
      nextMap.set(item.game_code.toLowerCase(), { game: item });
    }
    (item.game_List || []).forEach((subItem) => {
      if (!subItem?.game_code) {
        return;
      }
      const subGameCode = subItem.game_code.toLowerCase();
      if (!nextMap.has(subGameCode)) {
        nextMap.set(subGameCode, {
          game: subItem,
          parentGame: item,
        });
      }
    });
  });
  return nextMap;
};

/**
 * 归一化实体类型：
 * - 优先读取 game_type，其次 data_frame
 * - 多类型时补充 combine
 * - 无法识别时默认 mobile
 */
const normalizeEntityTypes = (game: IGameItem): EntityType[] => {
  const rawTypeList = `${game?.game_type || game?.data_frame || ''}`
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  if (rawTypeList.length === 0) {
    return [EntityType.Mobile];
  }

  const allTypes = new Set<EntityType>();
  rawTypeList.forEach((item) => {
    if (item === EntityType.Mobile) {
      allTypes.add(EntityType.Mobile);
      return;
    }
    if (item === EntityType.PC) {
      allTypes.add(EntityType.PC);
      return;
    }
    if (item === EntityType.Console) {
      allTypes.add(EntityType.Console);
      return;
    }
    if (item === EntityType.Company) {
      allTypes.add(EntityType.Company);
    }
  });

  if (allTypes.size > 1) {
    allTypes.add(EntityType.Combine);
  }

  if (allTypes.size === 0) {
    return [EntityType.Mobile];
  }
  return Array.from(allTypes);
};

/**
 * 兼容完整 URL 与相对路径 URL。
 * - 完整 URL 直接解析
 * - 相对路径使用当前站点 origin 解析
 */
const normalizeUrl = (rawUrl: string): URL | null => {
  try {
    return new URL(rawUrl);
  } catch (error) {
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      return new URL(rawUrl, window.location.origin);
    } catch (nestedError) {
      return null;
    }
  }
};

/**
 * 从 query 中尝试取得 game_code：
 * 兼容 game_code / gameCode / game / game_id / gameId
 */
const extractGameCodeFromQuery = (url: URL): string => {
  const params = url.searchParams;
  const gameCodeFromQuery =
    params.get('game_code') ||
    params.get('gameCode') ||
    params.get('game') ||
    params.get('game_id') ||
    params.get('gameId');
  return gameCodeFromQuery ? gameCodeFromQuery.trim() : '';
};

/**
 * 单词首字母大写。
 */
const capitalizeWord = (word: string): string => {
  if (!word) {
    return '';
  }
  return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
};

/**
 * 把单个 path 段拆成可读的英文单词序列，并首字母大写。
 * 规则与 home 端 `splitSegmentToWords` 对齐：
 * - camelCase / PascalCase 自动按大小写切分
 * - `-` / `_` 视为分隔符
 */
const splitSegmentToCapitalizedWords = (segment: string): string[] => {
  if (!segment) {
    return [];
  }
  return segment
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(capitalizeWord);
};

/**
 * 把 realPath 各段做首字母大写处理后用空格拼接。
 * 例：'overview/daily' -> 'Overview Daily'
 */
const formatRealPathToPageName = (realPath: string): string => {
  return realPath
    .split('/')
    .map((item) => item.trim())
    .filter(Boolean)
    .flatMap((seg) => splitSegmentToCapitalizedWords(seg))
    .join(' ');
};

/**
 * 提取页面名称（page_name）：
 * - 命中实体且有 realPath：`<游戏名>: <Realpath capitalized>`（每词首字母大写、空格连接）
 * - 命中实体无 realPath：`<游戏名>`
 * - 未命中实体：仅返回 realPath / pathname 大写化结果
 *
 * 格式约定（与首页设计对齐）：
 * - 第一个名称之后用 `:` + 空格分隔
 * - 之后所有段用空格连接，不再使用 `/`
 */
const buildPageName = (
  pathname: string,
  realPath: string,
  entityName?: string,
): string => {
  const realPathPart = realPath
    ? formatRealPathToPageName(realPath)
    : formatRealPathToPageName(pathname);

  if (entityName && realPathPart) {
    return `${entityName} ${realPathPart}`;
  }
  return entityName || realPathPart;
};

/**
 * 解析收藏 URL，返回协议约定字段。
 *
 * 经分 URL 形态：
 *   /[v\d/]?dashboard/game/<game_code>/<realPath...>?<query>
 */
export const analysisUrlInfo = async (
  params: IAnalysisUrlInfoParams,
): Promise<IAnalysisUrlInfoResult> => {
  const rawUrl = params?.url?.trim();
  if (!rawUrl) {
    return {
      success: false,
      permission: Permission.NoPermission,
      status: Status.Unavailable,
      page_url: '',
    };
  }

  const parsedUrl = normalizeUrl(rawUrl);
  if (!parsedUrl) {
    return {
      success: false,
      permission: Permission.NoPermission,
      status: Status.Unavailable,
      page_url: rawUrl,
    };
  }

  const { system, urlType, urlId, realPath } = parseUrlInfo(parsedUrl.pathname);
  const gameCodeFromPath =
    system === DASHBOARD_SYSTEM_CODE && urlType === DASHBOARD_URL_TYPE_GAME
      ? urlId
      : '';
  const gameCode = (
    gameCodeFromPath || extractGameCodeFromQuery(parsedUrl)
  ).toLowerCase();

  const normalizedUrl = parsedUrl.toString();

  const gameList = await fetchGameListCached();
  const gameMap = buildGameIndexMap(gameList);
  const matchedGameInfo = gameCode ? gameMap.get(gameCode) : undefined;

  if (!matchedGameInfo) {
    return {
      success: false,
      permission: Permission.NoPermission,
      status: Status.Unavailable,
      page_url: normalizedUrl,
      page_name: buildPageName(parsedUrl.pathname, realPath),
    };
  }

  // 注：matchedGameInfo 同时持有 parentGame（子游戏命中时），但权限只看子游戏
  // 自身的 is_access 即可。原因：/api/v1/dashboard/userMessage 返回的 game_List
  // 已经按用户权限过滤过 —— 子游戏能出现在 game_List 里就说明用户至少能看到它，
  // 不需要再额外校验父游戏权限。与 datalab 同源实现保持一致。
  const { game: matchedGame } = matchedGameInfo;
  const entityType = normalizeEntityTypes(matchedGame);
  const hasPermission = Boolean(matchedGame.is_access);
  const pageName = buildPageName(
    parsedUrl.pathname,
    realPath,
    matchedGame.game_name || '',
  );

  // 命中游戏但无权限：直接返回 success=false，由首页根据 permission 字段
  // 给出"无权限"提示并阻止加入收藏，避免无权限游戏也能被收藏成功。
  return {
    success: hasPermission,
    permission: hasPermission
      ? Permission.HasPermission
      : Permission.NoPermission,
    status: hasPermission ? Status.Available : Status.Unavailable,
    entity_type: entityType,
    entity_id: matchedGame.game_code,
    entity_name: matchedGame.game_name || '',
    entity_cover: matchedGame.cover || '',
    page_url: normalizedUrl,
    page_name: pageName,
    data: JSON.stringify({
      source: 'dashboard.userMessage.main_game',
      game_code: matchedGame.game_code,
    }),
  };
};

/**
 * 根据 entity_id 查询实体详情信息。
 */
export const getEntityDetailsById = async (
  params: IGetEntityDetailsByIdParams,
): Promise<IEntityDetailResult> => {
  const gameList = await fetchGameListCached();
  const gameMap = buildGameIndexMap(gameList);
  const entityId = `${params?.entity_id || ''}`.trim().toLowerCase();
  const matchedGameInfo = entityId ? gameMap.get(entityId) : undefined;

  if (!matchedGameInfo) {
    const fallbackEntityType =
      params?.entity_type && params.entity_type.length > 0
        ? params.entity_type
        : [EntityType.Mobile];
    return {
      entity_type: fallbackEntityType,
      entity_id: params?.entity_id || '',
      entity_name: '',
      entity_cover: '',
    };
  }

  const { game } = matchedGameInfo;
  const resolvedEntityType =
    params?.entity_type && params.entity_type.length > 0
      ? params.entity_type
      : normalizeEntityTypes(game);
  return {
    entity_type: resolvedEntityType,
    entity_id: game.game_code,
    entity_name: game.game_name || '',
    entity_cover: game.cover || '',
  };
};

/**
 * Dashboard 初始化收藏统一回到 daily overview，并用真实游戏 id 替换占位值。
 */
export const restoreDefaultShortcutItem = async (
  params: IRestoreDefaultUrlCollectionItemParams,
): Promise<IRestoreDefaultUrlCollectionItemResult> => {
  if (!params?.entity_id) {
    return {};
  }

  const entityId = params.entity_id;

  if (!entityId) {
    return {};
  }

  const nextUrl = DASHBOARD_DEFAULT_SHORTCUT_URL.replace(
    ':entityId',
    entityId,
  );
  const nextData = parseUrlCollectionData(params.data);
  const { realPath } = parseUrlInfo(nextUrl);
  const entityName = params.entity_name || '';
  const entityCover = params.entity_cover || '';
  const entityType = params.entity_type ? [params.entity_type] : [];
  const nextName = entityName
    ? buildPageName('', realPath, entityName)
    : params.name;

  return {
    url: nextUrl,
    name: nextName,
    data: mergeUrlCollectionData(params.data, {
      ...nextData,
      game_code: entityId,
      entity_id: entityId,
      entity_type: entityType,
      entity_name: entityName,
      entity_cover: entityCover,
      page_name: nextName || nextData.page_name || params.name,
      page_path: getUrlCollectionPagePath(nextUrl),
    }),
  };
};
